import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Globe, ExternalLink, Sparkles, MessageSquare, AtSign, Check, X, BookMarked, Sun, Moon, Camera } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import FeedbackForm from "../components/FeedbackForm";
import PushOptIn from "../components/PushOptIn";
import DailyConvergenceCard from "../components/DailyConvergenceCard";
import LoginPrompt from "../components/LoginPrompt";
import AvatarUploader from "../components/AvatarUploader";
import { useTheme } from "../hooks/useTheme";

const TRADITIONS = [
  "Christianity","Hinduism","Vedanta","Buddhism","Tibetan Buddhism","Sufism","Taoism","Tantra",
  "Stoicism","Platonism","Hermeticism","Kabbalah","Alchemy","Gnosticism",
  "Jungian Psychology","Modern Physics","Indigenous Australian","Mesoamerican",
  "Siberian Shamanism","Native American Shamanism","Secular / Skeptic","Spiritual but not religious",
];

const TAB_IDS = ["you", "settings"];

function readInitialTab(hash) {
  const h = (hash || "").replace(/^#/, "").toLowerCase();
  return TAB_IDS.includes(h) ? h : "you";
}

export default function Settings() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => readInitialTab(location.hash));

  // Preferences state
  const [traditions, setTraditions] = useState([]);
  const [oracleContext, setOracleContext] = useState("");
  const [oracleVoice, setOracleVoice] = useState("conversational");
  const [publicProfile, setPublicProfile] = useState(false);
  const [digestOptIn, setDigestOptIn] = useState(false);
  const [emailNotificationsOptIn, setEmailNotificationsOptIn] = useState(true);
  const [allowDms, setAllowDms] = useState(true);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Personal content state (unified Saves: concept pins + oracle saves)
  const [savesList, setSavesList] = useState([]);
  const [saving, setSaving] = useState(false);

  // Keep tab in sync with URL hash (deep-link friendly).
  useEffect(() => {
    setActiveTab(readInitialTab(location.hash));
  }, [location.hash]);

  useEffect(() => {
    if (loading) return;
    if (!user) return;  // anonymous visitors see the inline LoginPrompt below
    if (prefsLoaded) return;
    Promise.all([
      api.get("/users/me/preferences").then(({ data }) => {
        setTraditions(data.traditions || []);
        setOracleContext(data.oracle_context || "");
        setOracleVoice(data.oracle_voice || "conversational");
        setPublicProfile(!!data.public_profile);
        setDigestOptIn(!!data.digest_opt_in);
        setEmailNotificationsOptIn(data.email_notifications_opt_in !== false);
        setAllowDms(data.allow_dms !== false);
      }),
      api.get("/saves/me").then(({ data }) => setSavesList(data || [])).catch(() => {}),
    ]).catch((err) => console.error("settings load failed", err))
      .finally(() => setPrefsLoaded(true));
  }, [user, loading, navigate, prefsLoaded]);

  const toggle = (t) => setTraditions((cur) => cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/preferences", {
        traditions,
        oracle_context: oracleContext,
        oracle_voice: oracleVoice,
        public_profile: publicProfile,
        digest_opt_in: digestOptIn,
        email_notifications_opt_in: emailNotificationsOptIn,
        allow_dms: allowDms,
      });
      toast.success("Tuned.");
    } catch (err) { console.error(err); toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  if (!user) {
    // Differentiate the prompt copy between the "You" tab (profile) and the
    // "Settings" tab (preferences) so the call-to-action reads naturally.
    const isYouTab = activeTab === "you";
    return (
      <LoginPrompt
        title={isYouTab ? "Your archive, waiting." : "Tune your archive."}
        blurb={
          isYouTab
            ? "Sign in to claim a handle, save concepts, follow other seekers, and shape your own corner of the lexicon."
            : "Sign in to choose the traditions that feel like home, tune the Oracle's voice, and set how you want Correlate to reach you."
        }
      />
    );
  }

  return (
    <div data-testid="settings-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-20">
      {activeTab === "you" && (
        <YouTab
          user={user}
          savesList={savesList}
        />
      )}

      {activeTab === "settings" && (
        <SettingsTab
          user={user}
          refresh={refresh}
          prefsLoaded={prefsLoaded}
          traditions={traditions}
          toggleTradition={toggle}
          oracleContext={oracleContext}
          setOracleContext={setOracleContext}
          oracleVoice={oracleVoice}
          setOracleVoice={setOracleVoice}
          publicProfile={publicProfile}
          setPublicProfile={setPublicProfile}
          digestOptIn={digestOptIn}
          setDigestOptIn={setDigestOptIn}
          emailNotificationsOptIn={emailNotificationsOptIn}
          setEmailNotificationsOptIn={setEmailNotificationsOptIn}
          allowDms={allowDms}
          setAllowDms={setAllowDms}
          sendingPreview={sendingPreview}
          setSendingPreview={setSendingPreview}
          saving={saving}
          save={save}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------- YOU TAB */

function YouTab({ user, savesList }) {
  // Aggregate counts for the two summary cards. The full saves list lives on
  // /saves and shelves live on /collections — this tab stays sparse so the
  // profile preview and feedback form aren't buried beneath cards.
  const [shelfCount, setShelfCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/shelves/me")
      .then(({ data }) => { if (!cancelled) setShelfCount(Array.isArray(data) ? data.length : 0); })
      .catch(() => { if (!cancelled) setShelfCount(0); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="you-tab">
      <div className="label mb-6 text-[hsl(var(--accent))]">Your archive</div>
      <h1 className="serif text-5xl text-foreground">
        Welcome back, {user.name?.split(" ")[0] || "seeker"}.
      </h1>
      <p className="mt-6 text-muted-foreground leading-relaxed">
        Everything you've kept — your saved correlations, Oracle conversations, and a private way to write the archivists.
      </p>

      {/* MY PROFILE PREVIEW — same shape as /u/<me>, so you see yourself as others do. */}
      <MyProfilePreview user={user} />

      {/* BIO */}
      <BioEditor />

      {/* FEEDBACK */}
      <section className="mt-16" data-testid="feedback-section">
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare size={14} className="text-[hsl(var(--accent))]" />
          <div className="label">Talk to the archivists</div>
        </div>
        <p className="serif italic text-muted-foreground mb-6 max-w-xl">
          Idea, bug, praise, or an out-loud thought — every note lands directly in our notification tray.
        </p>
        <FeedbackForm />
      </section>

      {/* SUMMARY LINK CARDS — keep this tab uncluttered; details live on their own pages */}
      <section className="mt-20 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border" data-testid="you-summary-links">
        <Link
          to="/saves"
          data-testid="link-all-saved-items"
          className="bg-card p-6 group transition-colors hover:bg-[hsl(var(--accent)/0.04)]"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="label flex items-center gap-2">
              <BookMarked size={11} /> All saved items
            </div>
            <span className="serif italic text-sm text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors">→</span>
          </div>
          <div className="serif text-3xl text-foreground">
            {savesList.length}
            <span className="serif italic text-base text-muted-foreground ml-2">
              {savesList.length === 1 ? "item" : "items"}
            </span>
          </div>
          <p className="serif italic text-sm text-muted-foreground mt-2">
            Concepts, Oracle responses, your correlations — kept together.
          </p>
        </Link>

        <Link
          to="/collections"
          data-testid="link-collections"
          className="bg-card p-6 group transition-colors hover:bg-[hsl(var(--accent)/0.04)]"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="label flex items-center gap-2">
              <BookMarked size={11} /> Collections
            </div>
            <span className="serif italic text-sm text-muted-foreground group-hover:text-[hsl(var(--primary))] transition-colors">→</span>
          </div>
          <div className="serif text-3xl text-foreground">
            {shelfCount === null ? "…" : shelfCount}
            <span className="serif italic text-base text-muted-foreground ml-2">
              {shelfCount === 1 ? "collection" : "collections"}
            </span>
          </div>
          <p className="serif italic text-sm text-muted-foreground mt-2">
            Group your saves into named shelves you can share.
          </p>
        </Link>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------- SETTINGS TAB */

function SettingsTab(props) {
  const {
    user, refresh, prefsLoaded,
    traditions, toggleTradition,
    oracleContext, setOracleContext,
    oracleVoice, setOracleVoice,
    publicProfile, setPublicProfile,
    digestOptIn, setDigestOptIn,
    emailNotificationsOptIn, setEmailNotificationsOptIn,
    allowDms, setAllowDms,
    sendingPreview, setSendingPreview,
    saving, save,
  } = props;

  return (
    <div data-testid="settings-tab">
      <div className="label mb-6 text-[hsl(var(--accent))]">Tune your archive</div>
      <h1 className="serif text-5xl text-foreground">Where are you coming from?</h1>
      <p className="mt-6 text-muted-foreground leading-relaxed">
        Optional. Anything you choose helps Correlate meet you where you are. Editable any time.
      </p>

      <section className="mt-14">
        <div className="label mb-4">Traditions that feel like home</div>
        {!prefsLoaded ? (
          <div className="text-muted-foreground italic serif">Tuning…</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="tradition-chips">
            {TRADITIONS.map((t) => {
              const on = traditions.includes(t);
              return (
                <button key={t} data-testid={`trad-${t}`} onClick={() => toggleTradition(t)}
                        className={`mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors
                          ${on ? "border-[hsl(var(--primary))] text-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)]"
                               : "border-border text-muted-foreground hover:border-[hsl(var(--primary))] hover:text-foreground"}`}>
                  {t}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="mt-14">
        <div className="label mb-4">A line for the Oracle (optional)</div>
        <textarea value={oracleContext} onChange={(e) => setOracleContext(e.target.value)}
                  rows={3} maxLength={500} data-testid="oracle-context"
                  placeholder="e.g. raised Catholic, drawn to Jung and Tao, currently grieving a parent…"
                  className="input-line w-full text-[15px] leading-relaxed resize-none" />
        <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2">
          {oracleContext.length}/500 — used quietly to attune the Oracle's tone.
        </div>
      </section>

      <OracleVoiceSection oracleVoice={oracleVoice} setOracleVoice={setOracleVoice} />

      {/* PUBLIC READING SHELF */}
      <section className="mt-14" data-testid="public-profile-section">
        <div className="label mb-4">Public reading shelf</div>
        <label className="flex items-start gap-4 cursor-pointer group paper p-5">
          <input
            type="checkbox"
            data-testid="public-profile-toggle"
            checked={publicProfile}
            onChange={(e) => setPublicProfile(e.target.checked)}
            className="mt-1 accent-[hsl(var(--primary))]"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {publicProfile
                ? <Globe size={14} className="text-[hsl(var(--primary))]" />
                : <Lock size={14} className="text-muted-foreground" />}
              <span className="serif text-lg text-foreground">
                {publicProfile ? "Public — others can find your shelf" : "Private — only you can see your shelf"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              When on, your public pins, reactions, and approved submissions appear together at
              {" "}<span className="mono text-[11px] text-foreground/80">/u/{user.handle || user.user_id}</span>{" "}
              for anyone to find — a curated reading shelf you can share. Off by default.
            </p>
            {publicProfile && (
              <Link
                to={`/u/${user.handle || user.user_id}`}
                data-testid="view-public-profile"
                className="mt-3 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))] link-underline"
              >
                <ExternalLink size={11} /> View as visitor
              </Link>
            )}
          </div>
        </label>

        {publicProfile && <HandlePicker user={user} onClaimed={refresh} />}
      </section>

      {/* APPEARANCE */}
      <AppearanceSection />

      {/* DIRECT MESSAGES */}
      <section className="mt-14" data-testid="dms-section">
        <div className="label mb-4">Direct messages</div>
        <label className="flex items-start gap-4 cursor-pointer paper p-5">
          <input
            type="checkbox"
            data-testid="allow-dms-toggle"
            checked={allowDms}
            onChange={(e) => setAllowDms(e.target.checked)}
            className="mt-1 accent-[hsl(var(--primary))]"
          />
          <div className="flex-1">
            <div className="serif text-lg text-foreground">
              {allowDms ? "On — other readers can write to you privately" : "Off — your inbox is closed"}
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              One-to-one letters between seekers, capped at 2,000 characters. Notifications arrive via the bell and push only — never email.
            </p>
          </div>
        </label>
      </section>

      {/* WEEKLY DIGEST */}
      <section className="mt-14" data-testid="digest-section">
        <div className="label mb-4">Weekly digest</div>
        <label className="flex items-start gap-4 cursor-pointer paper p-5">
          <input
            type="checkbox"
            data-testid="digest-toggle"
            checked={digestOptIn}
            onChange={(e) => setDigestOptIn(e.target.checked)}
            className="mt-1 accent-[hsl(var(--primary))]"
          />
          <div className="flex-1">
            <div className="serif text-lg text-foreground">
              {digestOptIn ? "On — Sunday mornings" : "Off"}
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              A short, hand-picked email of the week's most resonant correlations, tuned to your traditions.
              Sent Sunday at 8am UTC. One-click unsubscribe in every email.
            </p>
            {digestOptIn && (
              <button
                type="button"
                data-testid="digest-preview"
                disabled={sendingPreview}
                onClick={async () => {
                  setSendingPreview(true);
                  try {
                    const { data } = await api.post("/digest/preview");
                    if (data?.sent) toast.success("Preview sent — check your inbox.");
                    else if (data?.reason === "no_items") toast.error("Nothing to digest yet. Pick a few traditions or read a few concepts first.");
                    else if (data?.hint) toast.error(data.hint);
                    else toast.error("Could not send preview.");
                  } catch { toast.error("Could not send preview."); }
                  finally { setSendingPreview(false); }
                }}
                className="mt-3 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--primary))] link-underline disabled:opacity-50"
              >
                {sendingPreview ? "Sending…" : "Send me a preview now →"}
              </button>
            )}
          </div>
        </label>
      </section>

      {/* EMAIL MIRRORS */}
      <section className="mt-10" data-testid="email-notifications-section">
        <div className="label mb-4">Email mirrors of notifications</div>
        <label className="flex items-start gap-4 cursor-pointer paper p-5">
          <input
            type="checkbox"
            data-testid="email-notifications-toggle"
            checked={emailNotificationsOptIn}
            onChange={(e) => setEmailNotificationsOptIn(e.target.checked)}
            className="mt-1 accent-[hsl(var(--primary))]"
          />
          <div className="flex-1">
            <div className="serif text-lg text-foreground">
              {emailNotificationsOptIn ? "On — mirror notifications to email" : "Off — bell only"}
            </div>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              When someone shares, replies, follows, or recommends a correlation to you, we'll also send a short email — so the archive can find you even when you're not in the app. The bell (in-app) is always on.
            </p>
          </div>
        </label>
      </section>

      {/* PUSH NOTIFICATIONS */}
      <section className="mt-10" data-testid="push-notifications-section">
        <div className="label mb-4">Push notifications</div>
        <PushOptIn />
      </section>

      {/* DAILY CONVERGENCE */}
      <section className="mt-10" data-testid="daily-convergence-section">
        <div className="label mb-4">Daily Convergence</div>
        <DailyConvergenceCard />
      </section>

      {/* SAVE */}
      <div className="mt-16 hairline-t pt-8">
        <button onClick={save} disabled={saving || !prefsLoaded} data-testid="settings-save" className="btn-gold disabled:opacity-40">
          {saving ? "Tuning…" : "Save"}
        </button>
      </div>

      {/* REPLAY TOUR */}
      <section className="mt-16" data-testid="replay-tour-section">
        <div className="label mb-4">Take the tour again</div>
        <button
          type="button"
          data-testid="replay-tour"
          onClick={() => {
            localStorage.removeItem("cor_tour_v3");
            localStorage.removeItem("cor_tour_v4");
            toast.success("Heading back to the threshold…");
            setTimeout(() => { window.location.href = "/"; }, 400);
          }}
          className="btn-ghost inline-flex items-center gap-2"
        >
          <Sparkles size={12} />
          Replay the guided walk
        </button>
        <p className="mt-3 text-sm text-muted-foreground italic">
          We'll start you back at the threshold and walk you through every room.
        </p>
      </section>

      {/* SIGN OUT */}
      <SignOutSection />

      {/* LEGAL — required for app-store listings, kept quiet here */}
      <section className="mt-12 hairline-t pt-6 flex items-center gap-5 text-muted-foreground/80" data-testid="legal-links">
        <Link to="/privacy" data-testid="settings-privacy-link" className="mono text-[10px] uppercase tracking-[0.22em] hover:text-foreground transition-colors">
          Privacy
        </Link>
        <Link to="/terms" data-testid="settings-terms-link" className="mono text-[10px] uppercase tracking-[0.22em] hover:text-foreground transition-colors">
          Terms
        </Link>
        <a href="mailto:privacy@correlate.online" className="mono text-[10px] uppercase tracking-[0.22em] hover:text-foreground transition-colors">
          Contact
        </a>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------- BIO EDITOR */

function MyProfilePreview({ user }) {
  const { refresh: refreshAuth } = useAuth();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploaderOpen, setUploaderOpen] = React.useState(false);

  const slug = user?.handle || user?.user_id;

  React.useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchProfile = () => {
      setLoading(true);
      api.get("/users/me/profile")
        .then(({ data: p }) => { if (!cancelled) setData(p); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    fetchProfile();
    // Re-fetch when other panels in the You tab update profile-shaped data
    // (e.g. BioEditor dispatches "correlate:profile-changed" after saving).
    const onChange = () => fetchProfile();
    window.addEventListener("correlate:profile-changed", onChange);
    return () => {
      cancelled = true;
      window.removeEventListener("correlate:profile-changed", onChange);
    };
  }, [user]);

  if (!user) return null;
  if (loading) {
    return (
      <div className="mt-10 paper p-6 serif italic text-muted-foreground">Loading your profile preview…</div>
    );
  }
  if (!data) return null;

  const counts = [
    { label: "follower", value: data.followers_count || 0, plural: "followers" },
    { label: "following", value: data.following_count || 0, plural: "following" },
    { label: "pin", value: (data.pins || []).length, plural: "pins" },
    { label: "reaction", value: (data.reactions || []).length, plural: "reactions" },
    { label: "contribution", value: (data.submissions || []).length, plural: "contributions" },
    { label: "oracle save", value: (data.oracle_saves || []).length, plural: "oracle saves" },
  ];

  return (
    <section data-testid="my-profile-preview" className="mt-10 paper p-6 sm:p-8">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))]">
          How others see you
        </div>
        <Link
          to={`/u/${slug}`}
          data-testid="view-public-profile"
          className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-[hsl(var(--primary))] inline-flex items-center gap-1.5"
        >
          Open your profile <ExternalLink size={11} />
        </Link>
      </div>

      <div className="flex items-start gap-5">
        <button
          type="button"
          data-testid="my-avatar-edit"
          onClick={() => setUploaderOpen(true)}
          title="Change your profile picture"
          aria-label="Change your profile picture"
          className="relative group flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
        >
          {data.picture ? (
            <img src={data.picture} alt="" className="w-20 h-20 rounded-full ring-1 ring-border object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-3xl text-foreground">
              {(data.name || "?")[0]?.toUpperCase() || "?"}
            </div>
          )}
          <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <Camera size={18} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.8} />
          </span>
        </button>
        <div className="flex-1 min-w-0">
          <div className="serif text-3xl text-foreground leading-tight">{data.name}</div>
          {data.handle && (
            <div className="mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">@{data.handle}</div>
          )}
          {data.is_public === false && (
            <div className="mt-3 inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <Lock size={11} /> Your shelf is private
            </div>
          )}
        </div>
      </div>

      <AvatarUploader
        open={uploaderOpen}
        onClose={() => setUploaderOpen(false)}
        canReset={!!user?.has_google_picture}
        onUploaded={(url) => {
          setData((d) => (d ? { ...d, picture: url } : d));
          refreshAuth?.();
          window.dispatchEvent(new Event("correlate:profile-changed"));
        }}
      />

      {data.bio ? (
        <p
          data-testid="my-profile-bio"
          className="mt-5 serif italic text-foreground/85 leading-relaxed whitespace-pre-wrap"
        >
          {data.bio}
        </p>
      ) : (
        <p className="mt-5 serif italic text-muted-foreground/80">
          You haven&apos;t written a bio yet — add one below to introduce yourself.
        </p>
      )}

      <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 hairline-t pt-5">
        {counts.map(({ label, value, plural }) => (
          <li key={label} data-testid={`my-count-${label.replace(/\s+/g, '-')}`} className="flex items-baseline gap-2">
            <span className="serif text-2xl text-foreground tabular-nums">{value}</span>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {value === 1 ? label : plural}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}


/* -------------------------------------------------------------- BIO EDITOR */

function BioEditor() {
  const [bio, setBio] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    api.get("/users/me/preferences")
      .then(({ data }) => setBio(data.bio || ""))
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/preferences", { bio });
      setDirty(false);
      toast.success("Bio saved.");
      // Let MyProfilePreview (and any other panel listening) refresh.
      window.dispatchEvent(new CustomEvent("correlate:profile-changed"));
    } catch {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-16" data-testid="bio-section">
      <div className="label mb-3">Your bio</div>
      <p className="serif italic text-muted-foreground mb-4 max-w-xl">
        A short line others see when they visit your profile. Up to 280 characters. Public profiles only.
      </p>
      <textarea
        data-testid="bio-input"
        value={bio}
        disabled={!loaded}
        onChange={(e) => { setBio(e.target.value.slice(0, 280)); setDirty(true); }}
        rows={3}
        maxLength={280}
        placeholder="A curious reader of bridges between traditions — drawn to Jung, Sufi mystics, and the quieter parts of the New Testament."
        className="input-line w-full text-[15px] leading-relaxed resize-none"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {bio.length}/280
        </span>
        <button
          type="button"
          data-testid="bio-save"
          onClick={save}
          disabled={!loaded || !dirty || saving}
          className="btn-gold disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save bio"}
        </button>
      </div>
    </section>
  );
}


function SignOutSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <section className="mt-16 hairline-t pt-10" data-testid="signout-section">
      <div className="label mb-4">Leave the archive</div>
      <button
        type="button"
        data-testid="settings-logout"
        onClick={async () => { await logout(); navigate("/"); }}
        className="btn-ghost"
      >
        Sign out
      </button>
      <p className="mt-3 text-sm text-muted-foreground italic">
        You can come back any time — your saves and conversations stay put.
      </p>
    </section>
  );
}

/* -------------------------------------------------------------- HANDLE PICKER */

function HandlePicker({ user, onClaimed }) {
  const [value, setValue] = useState(user.handle || "");
  const [status, setStatus] = useState({ state: "idle", reason: null });
  const [saving, setSaving] = useState(false);
  const isCurrent = (value || "").toLowerCase() === (user.handle || "").toLowerCase();

  useEffect(() => {
    const v = (value || "").trim().toLowerCase();
    if (!v) { setStatus({ state: "idle", reason: null }); return; }
    if (v === (user.handle || "").toLowerCase()) {
      setStatus({ state: "current", reason: null });
      return;
    }
    setStatus({ state: "checking", reason: null });
    const t = setTimeout(() => {
      api.get(`/users/handle-available?q=${encodeURIComponent(v)}`)
        .then(({ data }) => setStatus(data.available
          ? { state: "ok", reason: null }
          : { state: "taken", reason: data.reason || "Taken" }))
        .catch(() => setStatus({ state: "error", reason: "Could not check" }));
    }, 350);
    return () => clearTimeout(t);
  }, [value, user.handle]);

  const claim = async () => {
    setSaving(true);
    try {
      await api.post("/users/me/handle", { handle: value.trim().toLowerCase() });
      toast.success("Handle claimed.");
      onClaimed && onClaimed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not claim handle.");
    } finally { setSaving(false); }
  };

  const suggested = !user.handle && user.name
    ? user.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 20)
    : null;

  return (
    <div data-testid="handle-picker" className="mt-6 paper p-5">
      <div className="flex items-center gap-2 mb-3">
        <AtSign size={13} className="text-[hsl(var(--primary))]" />
        <div className="label">Your handle</div>
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
        Pick a short, memorable name for your profile link.
        3-20 lowercase letters, digits, and dashes.
        {suggested && !user.handle && (
          <> Try <button type="button" data-testid="handle-suggest"
                        onClick={() => setValue(suggested)}
                        className="mono text-[11px] text-[hsl(var(--primary))] link-underline">{suggested}</button>.</>
        )}
      </p>
      <div className="flex items-center gap-2 hairline px-3 py-2">
        <span className="mono text-sm text-muted-foreground select-none">/u/</span>
        <input
          data-testid="handle-input"
          value={value}
          onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          maxLength={20}
          placeholder="your-handle"
          className="flex-1 bg-transparent border-0 outline-none text-foreground placeholder:text-muted-foreground/60 mono text-sm"
        />
        {status.state === "ok"      && <Check size={14} className="text-emerald-600" data-testid="handle-ok" />}
        {status.state === "current" && <Check size={14} className="text-muted-foreground" data-testid="handle-current" />}
        {status.state === "taken"   && <X size={14} className="text-[hsl(var(--destructive))]" data-testid="handle-taken" />}
        {status.state === "checking" && <span className="mono text-[10px] text-muted-foreground">…</span>}
      </div>
      {status.state === "taken" && status.reason && (
        <div className="mt-2 mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--destructive))]">
          {status.reason}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          data-testid="handle-claim"
          disabled={saving || status.state !== "ok" || isCurrent}
          onClick={claim}
          className="btn-gold disabled:opacity-40"
        >
          {saving ? "Claiming…" : user.handle ? "Update handle" : "Claim handle"}
        </button>
        {user.handle && (
          <span className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Current: @{user.handle}
          </span>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- ORACLE VOICE */

// Voice options exposed in Settings. Keys MUST match the backend's ORACLE_VOICES
// dict in /app/backend/core/schemas.py — the backend re-validates and falls back
// to "conversational" if anything unrecognised arrives.
const ORACLE_VOICE_OPTIONS = [
  {
    key: "conversational",
    name: "Conversational",
    blurb: "A friend across a small table. Short paragraphs, images before definitions.",
  },
  {
    key: "scholarly",
    name: "Scholarly",
    blurb: "Measured and rigorous. Inline citations, source labels, two-line close.",
  },
  {
    key: "storyteller",
    name: "Storyteller",
    blurb: "Opens with a scene. Lets correlations emerge through narrative.",
  },
  {
    key: "poetic",
    name: "Poetic",
    blurb: "Rhythm, breath, metaphor. Short lines. Image-led.",
  },
  {
    key: "plain",
    name: "Plain",
    blurb: "Direct and concise. Says what it means and stops.",
  },
];

function OracleVoiceSection({ oracleVoice, setOracleVoice }) {
  return (
    <section className="mt-14" data-testid="oracle-voice-section">
      <div className="label mb-4">Oracle voice</div>
      <p className="text-muted-foreground text-[14px] leading-relaxed mb-5 max-w-2xl">
        How the Oracle speaks to you. Change anytime — it applies to every
        future response.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {ORACLE_VOICE_OPTIONS.map((v) => {
          const on = oracleVoice === v.key;
          return (
            <button
              key={v.key}
              type="button"
              onClick={() => setOracleVoice(v.key)}
              data-testid={`oracle-voice-${v.key}`}
              aria-pressed={on}
              className={`text-left p-4 border transition-colors ${
                on
                  ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.06)]"
                  : "border-border hover:border-[hsl(var(--primary))] bg-background/40"
              }`}
            >
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span
                  className={`serif text-lg ${
                    on ? "text-[hsl(var(--primary))]" : "text-foreground"
                  }`}
                >
                  {v.name}
                </span>
                {on && (
                  <span className="mono text-[9px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">
                    Selected
                  </span>
                )}
              </div>
              <p className="text-[13px] text-muted-foreground leading-relaxed">
                {v.blurb}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function AppearanceSection() {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: "light", label: "Light", Icon: Sun,  hint: "Sunrise / honey — the default." },
    { value: "dark",  label: "Dark",  Icon: Moon, hint: "Dusk / honey — warmer than a neutral grey dark mode." },
  ];
  return (
    <section className="mt-14" data-testid="appearance-section">
      <div className="label mb-4">Appearance</div>
      <div className="paper p-5">
        <div className="serif text-lg text-foreground mb-4">Theme</div>
        <div
          role="radiogroup"
          aria-label="Theme"
          data-testid="theme-picker"
          className="grid grid-cols-2 gap-3"
        >
          {options.map(({ value, label, Icon, hint }) => {
            const active = theme === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`theme-option-${value}`}
                onClick={() => setTheme(value)}
                className={`p-4 border transition-all text-left ${
                  active
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)]"
                    : "border-border hover:border-[hsl(var(--primary)/0.5)]"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon size={16} className={active ? "text-[hsl(var(--primary))]" : "text-muted-foreground"} />
                  <span className="serif text-base text-foreground">{label}</span>
                  {active && (
                    <Check size={14} className="ml-auto text-[hsl(var(--primary))]" aria-hidden="true" />
                  )}
                </div>
                <p className="mt-2 text-[13px] text-muted-foreground leading-relaxed">{hint}</p>
              </button>
            );
          })}
        </div>
        <p className="mt-4 text-[12px] text-muted-foreground leading-relaxed">
          Saved on this device. The Oracle keeps its own dusk atmosphere in either mode.
        </p>
      </div>
    </section>
  );
}


